import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { MemberRole, Member } from "../../types/memberTypes"
import { getPermissionsByRole } from "./useFormPermissions"
import { updateMemberPermissions } from "@/utils/firestore/users/memberPermissions"

export const useMemberEditing = (
  setMembers: React.Dispatch<React.SetStateAction<Member[]>>,
  effectivePermissionForActions: boolean = false,
  portfolioId?: string
) => {
  const { toast } = useToast()
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null)

  // Edit member actions
  const handleEditClick = (memberId: string) => {
    if (!effectivePermissionForActions) {
      toast({
        title: "Permission Denied",
        description: "You do not have permission to edit members.",
      })
      return
    }

    setEditingMemberId(memberId)
  }

  const handleCancelEdit = () => {
    setEditingMemberId(null)
  }

  const handleSaveEdit = () => {
    toast({
      title: "Changes Saved",
      description: "Member information has been updated.",
    })
    setEditingMemberId(null)
  }

  // Role and permission changes
  const handleRoleChange = (
    memberId: string,
    role: MemberRole,
    setMemberToPromote: (member: Member) => void,
    setShowTransferOwnershipDialog: (show: boolean) => void,
    members: Member[]
  ) => {
    if (role === "Primary Owner") {
      // Show ownership transfer dialog
      const memberToPromote = members.find((m) => m.id === memberId)
      if (memberToPromote) {
        setMemberToPromote(memberToPromote)
        setShowTransferOwnershipDialog(true)
      }
      return
    }

    setMembers((prev) =>
      prev.map((member) => {
        if (member.id === memberId) {
          // If changing role, update permissions based on role
          const updatedPermissions = getPermissionsByRole(role)

          return {
            ...member,
            role,
            permissions: updatedPermissions,
          }
        }
        return member
      })
    )
  }

  const handlePermissionChange = async (
    memberId: string,
    permissionId: string,
    checked: boolean
  ) => {
    // Update local state first for immediate UI feedback
    setMembers((prev) =>
      prev.map((member) => {
        if (member.id === memberId) {
          return {
            ...member,
            permissions: member.permissions.map((p) => {
              if (p.id === permissionId) {
                return { ...p, checked }
              }
              return p
            }),
          }
        }
        return member
      })
    )

    // If we have a portfolioId, update permissions in Firestore
    if (portfolioId) {
      try {
        const success = await updateMemberPermissions(
          portfolioId,
          memberId,
          permissionId,
          checked
        )

        if (success) {
          console.log(
            `Successfully updated permission ${permissionId} for member ${memberId}`
          )
        } else {
          // If Firestore update fails, show error toast
          toast({
            title: "Error Updating Permissions",
            description:
              "There was an error updating permissions. Please try again.",
            variant: "destructive",
          })
        }
      } catch (error) {
        console.error("Error updating permissions:", error)
        toast({
          title: "Error Updating Permissions",
          description:
            "There was an error updating permissions. Please try again.",
          variant: "destructive",
        })
      }
    } else {
      console.warn("No portfolioId provided, skipping Firestore update")
    }
  }

  return {
    editingMemberId,
    setEditingMemberId,
    handleEditClick,
    handleCancelEdit,
    handleSaveEdit,
    handleRoleChange,
    handlePermissionChange,
  }
}
